import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, RefreshCw, CheckCircle, XCircle,
  MessageSquare, Bell, Mail, Send, Settings, Eye, EyeOff,
  Smartphone, AlertTriangle, TestTube
} from 'lucide-react';

// Provider type icons
const providerIcons = {
  whatsapp: MessageSquare,
  push: Bell,
  email: Mail,
  sms: Smartphone,
  telegram: Send,
  meta_cloud: MessageSquare,
};

// Status badge
const StatusBadge = ({ status }) => {
  const styles = {
    healthy: 'bg-green-100 text-green-700',
    degraded: 'bg-yellow-100 text-yellow-700',
    unavailable: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-700',
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.unknown}`}>
      {status === 'healthy' ? 'Attivo' : status === 'degraded' ? 'Degradato' : status === 'unavailable' ? 'Errore' : 'Sconosciuto'}
    </span>
  );
};

// Provider Card
const ProviderCard = ({ provider, onEdit, onDelete, onTest, onTestMessage }) => {
  const Icon = providerIcons[provider.type] || Settings;
  
  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
      provider.is_legacy ? 'border-amber-300' : 'border-gray-200'
    }`}>
      {/* Legacy Warning Banner */}
      {provider.is_legacy && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4" />
          <span>Provider legacy - richiede migrazione</span>
        </div>
      )}
      
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              provider.is_legacy ? 'bg-amber-100' : provider.enabled ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <Icon className={`w-6 h-6 ${
                provider.is_legacy ? 'text-amber-600' : provider.enabled ? 'text-green-600' : 'text-gray-400'
              }`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{provider.name}</h3>
              <p className="text-sm text-gray-500 capitalize">{provider.type}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {provider.is_legacy && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                Legacy
              </span>
            )}
            {provider.is_default && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                Default
              </span>
            )}
            {provider.sandbox && !provider.is_legacy && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                Sandbox
              </span>
            )}
            <StatusBadge status={provider.status} />
          </div>
        </div>
        
        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">API URL</span>
            <span className="text-gray-700 truncate max-w-[200px]" title={provider.api_url}>
              {provider.api_url || '-'}
            </span>
          </div>
          {provider.type === 'whatsapp' && (
            <div className="flex justify-between">
              <span className="text-gray-500">Numero Mittente</span>
              <span className={provider.sender_number ? 'text-gray-700' : 'text-red-500'}>
                {provider.sender_number || provider.config?.sender_number || 'Non configurato'}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-500">Priorità</span>
            <span className="text-gray-700">{provider.priority}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Credenziali (API Key)</span>
            <span className={provider.has_credentials && !provider.is_legacy ? 'text-green-600' : 'text-red-600'}>
              {provider.is_legacy ? 'Legacy (da migrare)' : provider.has_credentials ? 'Configurate' : 'Mancanti'}
            </span>
          </div>
          {provider.last_successful_connection && (
            <div className="flex justify-between">
              <span className="text-gray-500">Ultima connessione OK</span>
              <span className="text-green-600 text-xs">{new Date(provider.last_successful_connection).toLocaleString('it-IT')}</span>
            </div>
          )}
          {provider.last_test && (
            <div className="flex justify-between">
              <span className="text-gray-500">Ultimo test</span>
              <span className="text-gray-700">{new Date(provider.last_test).toLocaleString('it-IT')}</span>
            </div>
          )}
          {provider.last_error && (
            <div className="mt-2 p-2 bg-red-50 rounded-lg text-xs text-red-700">
              {provider.last_error}
            </div>
          )}
          {provider.legacy_warning && (
            <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-start gap-2">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{provider.legacy_warning}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => onTest(provider.id)}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Test Connessione
          </button>
          <button
            onClick={() => onTestMessage(provider)}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
          >
            <Send className="w-4 h-4" />
            Invia Test
          </button>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => onEdit(provider)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Modifica"
          >
            <Edit className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={() => onDelete(provider.id)}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            title="Elimina"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Provider Form Modal
const ProviderModal = ({ isOpen, onClose, onSave, provider, providerTypes }) => {
  const [form, setForm] = useState({
    name: '',
    type: 'whatsapp',
    api_url: '',
    sender_number: '',
    enabled: true,
    priority: 1,
    is_default: false,
    sandbox: true,
    credentials: {}
  });
  const [showCredentials, setShowCredentials] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (provider) {
      setForm({
        name: provider.name || '',
        type: provider.type || 'whatsapp',
        api_url: provider.api_url || '',
        sender_number: provider.sender_number || provider.config?.sender_number || '',
        enabled: provider.enabled ?? true,
        priority: provider.priority || 1,
        is_default: provider.is_default ?? false,
        sandbox: provider.sandbox ?? true,
        credentials: {}
      });
    } else {
      setForm({
        name: '',
        type: 'whatsapp',
        api_url: 'https://whats-api.rcsoft.in/send-message',
        sender_number: '',
        enabled: true,
        priority: 1,
        is_default: false,
        sandbox: true,
        credentials: { api_key: '' }
      });
    }
  }, [provider]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form, provider?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Official MYRC API credentials - only api_key needed
  const credentialFields = {
    whatsapp: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
    ],
    email: [
      { key: 'smtp_host', label: 'SMTP Host', type: 'text' },
      { key: 'smtp_port', label: 'SMTP Port', type: 'text' },
      { key: 'smtp_user', label: 'SMTP User', type: 'text' },
      { key: 'smtp_password', label: 'SMTP Password', type: 'password' },
    ],
    push: [],
    sms: [
      { key: 'api_key', label: 'API Key', type: 'password' },
    ],
  };

  // Additional functional fields per provider type
  const functionalFields = {
    whatsapp: [
      { key: 'sender_number', label: 'Numero Mittente (WhatsApp)', type: 'tel', placeholder: '+39 333 1234567', required: true }
    ],
    email: [],
    push: [],
    sms: [
      { key: 'sender_number', label: 'Numero Mittente', type: 'tel', placeholder: '+39 333 1234567' }
    ],
  };

  const currentCredFields = credentialFields[form.type] || [];
  const currentFunctionalFields = functionalFields[form.type] || [];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900 text-lg">
            {provider ? 'Modifica Provider' : 'Nuovo Provider'}
          </h3>
          {form.type === 'whatsapp' && (
            <p className="text-xs text-gray-500 mt-1">
              API Ufficiale MYRC - POST https://whats-api.rcsoft.in/send-message
            </p>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nome Provider</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
              placeholder="Es: WhatsApp MYRC"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Tipo</label>
            <select
              value={form.type}
              onChange={(e) => setForm({...form, type: e.target.value, credentials: {}, sender_number: ''})}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
            >
              {providerTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">API URL</label>
            <input
              type="url"
              value={form.api_url}
              onChange={(e) => setForm({...form, api_url: e.target.value})}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
              placeholder={form.type === 'whatsapp' ? 'https://whats-api.rcsoft.in/send-message' : 'https://api.example.com/send'}
            />
            {form.type === 'whatsapp' && (
              <p className="text-xs text-gray-500 mt-1">Endpoint ufficiale MYRC: https://whats-api.rcsoft.in/send-message</p>
            )}
          </div>

          {/* Functional Fields (e.g., sender_number) */}
          {currentFunctionalFields.map(field => (
            <div key={field.key}>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type={field.type}
                value={form[field.key] || ''}
                onChange={(e) => setForm({...form, [field.key]: e.target.value})}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                placeholder={field.placeholder}
                required={field.required}
              />
              {field.key === 'sender_number' && form.type === 'whatsapp' && (
                <p className="text-xs text-gray-500 mt-1">Numero WhatsApp registrato su MYRC da cui verranno inviati i messaggi</p>
              )}
            </div>
          ))}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Priorità</label>
              <input
                type="number"
                min="1"
                max="10"
                value={form.priority}
                onChange={(e) => setForm({...form, priority: parseInt(e.target.value)})}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
              />
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({...form, enabled: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300 text-[#C5A059] focus:ring-[#C5A059]"
                />
                <span className="text-sm text-gray-700">Attivo</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.sandbox}
                  onChange={(e) => setForm({...form, sandbox: e.target.checked})}
                  className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                />
                <span className="text-sm text-gray-700">Sandbox</span>
              </label>
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({...form, is_default: e.target.checked})}
              className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Provider predefinito per questo tipo</span>
          </label>

          {/* Credentials */}
          {currentCredFields.length > 0 && (
            <div className="border-t border-gray-200 pt-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Credenziali</h4>
                <button
                  type="button"
                  onClick={() => setShowCredentials(!showCredentials)}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {showCredentials ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showCredentials ? 'Nascondi' : 'Mostra'}
                </button>
              </div>
              <div className="space-y-3">
                {currentCredFields.map(field => (
                  <div key={field.key}>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">{field.label}</label>
                    <input
                      type={showCredentials ? 'text' : field.type}
                      value={form.credentials[field.key] || ''}
                      onChange={(e) => setForm({
                        ...form,
                        credentials: {...form.credentials, [field.key]: e.target.value}
                      })}
                      className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
                      placeholder={provider ? '••••••• (lascia vuoto per non modificare)' : ''}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Le credenziali sono crittografate e non vengono mai esposte nel frontend.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 bg-[#C5A059] hover:bg-[#B8934D] rounded-lg text-white font-medium disabled:opacity-50 transition-colors shadow-sm"
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Test Message Modal
const TestMessageModal = ({ isOpen, onClose, provider, onSend }) => {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('Questo è un messaggio di test da La Maisonette di Paestum.');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen || !provider) return null;

  const handleSend = async () => {
    setSending(true);
    setResult(null);
    try {
      const res = await onSend(provider.id, phone, message);
      setResult(res);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900 text-lg">Test Invio - {provider.name}</h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Numero di telefono</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900"
              placeholder="+39 333 1234567"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">Messaggio</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 resize-none"
              rows={3}
            />
          </div>

          {provider.sandbox && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Provider in modalità Sandbox - il messaggio non verrà realmente inviato
            </div>
          )}

          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {result.success ? (
                <>
                  <CheckCircle className="w-4 h-4 inline mr-1" /> 
                  {result.response?.sandbox ? 'Invio simulato (Sandbox)' : 'Invio riuscito!'}
                </>
              ) : (
                <><XCircle className="w-4 h-4 inline mr-1" /> Errore: {result.response?.error || 'Errore sconosciuto'}</>
              )}
              
              {/* Show delivery log fields */}
              {result.response && result.success && (
                <div className="mt-2 space-y-1 text-xs">
                  {result.response.provider_message_id && (
                    <div><strong>Message ID:</strong> {result.response.provider_message_id}</div>
                  )}
                  {result.response.remoteJid && (
                    <div><strong>Remote JID:</strong> {result.response.remoteJid}</div>
                  )}
                  {result.response.timestamp && (
                    <div><strong>Timestamp:</strong> {new Date(result.response.timestamp * 1000).toLocaleString('it-IT')}</div>
                  )}
                  {result.response.delivery_status && (
                    <div><strong>Stato:</strong> {result.response.delivery_status}</div>
                  )}
                  {result.response.execution_time_ms && (
                    <div><strong>Tempo esecuzione:</strong> {result.response.execution_time_ms}ms</div>
                  )}
                </div>
              )}
              
              {result.response && !result.success && (
                <pre className="mt-2 text-xs overflow-auto max-h-32">{JSON.stringify(result.response, null, 2)}</pre>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium"
            >
              Chiudi
            </button>
            <button
              onClick={handleSend}
              disabled={sending || !phone}
              className="flex-1 py-3 bg-green-600 hover:bg-green-500 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Invio...' : 'Invia Test'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function ProvidersPage() {
  const { token } = useAuth();
  const [providers, setProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [testMessageProvider, setTestMessageProvider] = useState(null);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Errore nel caricamento provider');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderTypes = async () => {
    try {
      const response = await fetch(`${API}/admin/communication/provider-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProviderTypes(data.types || []);
      }
    } catch (error) {
      console.error('Error fetching provider types:', error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProviders();
      fetchProviderTypes();
    }
  }, [token]);

  const handleSave = async (form, providerId) => {
    try {
      const url = providerId 
        ? `${API}/admin/communication/providers/${providerId}`
        : `${API}/admin/communication/providers`;
      
      // Clean empty credentials
      const cleanForm = {...form};
      if (cleanForm.credentials) {
        Object.keys(cleanForm.credentials).forEach(key => {
          if (!cleanForm.credentials[key]) {
            delete cleanForm.credentials[key];
          }
        });
        if (Object.keys(cleanForm.credentials).length === 0) {
          delete cleanForm.credentials;
        }
      }
      
      const response = await fetch(url, {
        method: providerId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanForm)
      });
      
      if (response.ok) {
        toast.success(providerId ? 'Provider aggiornato' : 'Provider creato');
        fetchProviders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const handleDelete = async (providerId) => {
    if (!confirm('Eliminare questo provider?')) return;
    
    try {
      const response = await fetch(`${API}/admin/communication/providers/${providerId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Provider eliminato');
        fetchProviders();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const handleTest = async (providerId) => {
    try {
      const response = await fetch(`${API}/admin/communication/providers/${providerId}/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      fetchProviders();
    } catch (error) {
      toast.error('Errore nel test connessione');
    }
  };

  const handleTestMessage = async (providerId, phone, message) => {
    try {
      const response = await fetch(`${API}/admin/communication/providers/${providerId}/send-test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider_id: providerId, phone, message })
      });
      
      return await response.json();
    } catch (error) {
      return { success: false, response: { error: error.message } };
    }
  };

  return (
    <AdminLayout title="Communication Providers">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Provider Comunicazione</h1>
            <p className="text-gray-500 mt-1">Configura i canali di comunicazione (WhatsApp, Push, Email, SMS)</p>
          </div>
          <button
            onClick={() => { setEditingProvider(null); setShowModal(true); }}
            className="px-4 py-2.5 bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuovo Provider
          </button>
        </div>

        {/* Provider Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun provider configurato</h3>
            <p className="text-gray-500 mb-4">Aggiungi un provider per iniziare a inviare comunicazioni automatiche</p>
            <button
              onClick={() => { setEditingProvider(null); setShowModal(true); }}
              className="px-4 py-2 bg-[#C5A059] hover:bg-[#B8934D] text-white rounded-lg font-medium"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Aggiungi Provider
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {providers.map(provider => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                onEdit={(p) => { setEditingProvider(p); setShowModal(true); }}
                onDelete={handleDelete}
                onTest={handleTest}
                onTestMessage={(p) => setTestMessageProvider(p)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <ProviderModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingProvider(null); }}
        onSave={handleSave}
        provider={editingProvider}
        providerTypes={providerTypes}
      />

      <TestMessageModal
        isOpen={!!testMessageProvider}
        onClose={() => setTestMessageProvider(null)}
        provider={testMessageProvider}
        onSend={handleTestMessage}
      />
    </AdminLayout>
  );
}
